import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ArrowLeft, MapPin, Phone, Clock, CheckCircle, AlertCircle, 
  User, Wrench, Calendar, DollarSign, Image as ImageIcon, 
  Loader2, Edit, Save, X, Copy, ExternalLink, Upload, Trash2,
  MessageCircle, Send
} from 'lucide-react';
import { ServiceAdvisorService } from '../../services/serviceAdvisorService';

const ComplaintDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
  
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [showPincodeModal, setShowPincodeModal] = useState(false);
  const [pincode, setPincode] = useState('');
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionData, setCompletionData] = useState({
    completionNotes: '',
    customerQuotedAmount: '',
    advancePayment: '',
    mechanicPayment: '',
    completionMedia: []
  });
  const [uploadingCompletion, setUploadingCompletion] = useState(false);
  const [showEditChooserModal, setShowEditChooserModal] = useState(false);
  const [showFinancialModal, setShowFinancialModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [financialEditData, setFinancialEditData] = useState({
    customerQuotedAmount: '',
    advancePayment: '',
    mechanicPayment: ''
  });
  const [customerEditData, setCustomerEditData] = useState({
    customerName: '',
    contactNumber: '',
    pincode: ''
  });
  const [isEditWindowActive, setIsEditWindowActive] = useState(false);
  const [editRemainingSeconds, setEditRemainingSeconds] = useState(0);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTransferAdvisorId, setSelectedTransferAdvisorId] = useState('');
  const [isTransferring, setIsTransferring] = useState(false);
  const [advisors, setAdvisors] = useState([]);

  const { data: complaintData, isLoading, refetch } = useQuery({
    queryKey: ['serviceAdvisorComplaintDetail', id],
    queryFn: async () => {
      const result = await ServiceAdvisorService.getComplaintById(id);
      return result.data;
    },
    enabled: !!id,
  });

  const { data: assignedMechanicRow } = useQuery({
    queryKey: ['serviceAdvisorAssignedMechanic', complaintData?.assigned_mechanic_id],
    queryFn: async () => {
      const res = await ServiceAdvisorService.getMechanicById(complaintData?.assigned_mechanic_id);
      return res.data;
    },
    enabled: !!complaintData?.assigned_mechanic_id,
  });

  const { data: timelineData } = useQuery({
    queryKey: ['serviceAdvisorComplaintTimeline', id],
    queryFn: async () => {
      const result = await ServiceAdvisorService.getComplaintTimeline(id);
      return result.data || [];
    },
    enabled: !!id,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ status, notes }) => ServiceAdvisorService.updateComplaintStatus(id, status, notes, user?.id),
    onSuccess: () => {
      alert('Status updated successfully!');
      setShowStatusModal(false);
      setNewStatus('');
      setStatusNotes('');
      refetch();
      queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
      queryClient.invalidateQueries(['serviceAdvisorComplaintTimeline', id]);
    },
    onError: (error) => {
      alert('Failed to update status: ' + error.message);
    },
  });

  const updatePincodeMutation = useMutation({
    mutationFn: (pincode) => ServiceAdvisorService.updateComplaintPincode(id, pincode),
    onSuccess: () => {
      alert('Pincode updated successfully!');
      setShowPincodeModal(false);
      setPincode('');
      refetch();
    },
    onError: (error) => {
      alert('Failed to update pincode: ' + error.message);
    },
  });

  const complaint = complaintData || {};
  const timeline = timelineData || [];

  const getStatusColor = (status) => {
    const s = status?.toLowerCase() || '';
    if (s === 'completed') return 'bg-green-100 text-green-700';
    if (s === 'in_progress') return 'bg-blue-100 text-blue-700';
    if (s === 'assigned') return 'bg-yellow-100 text-yellow-700';
    if (s === 'registered') return 'bg-purple-100 text-purple-700';
    return 'bg-gray-100 text-gray-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const handleCall = (phoneNumber) => {
    if (phoneNumber) {
      window.location.href = `tel:${phoneNumber}`;
    } else {
      alert('No phone number available');
    }
  };

  const handleWhatsAppLocation = () => {
    if (!complaint.location_link) {
      alert('Location link not available');
      return;
    }
    const message = `Please share your location: ${complaint.location_link}`;
    const whatsappUrl = `https://wa.me/${complaint.contact_number?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleTransferComplaint = async () => {
    if (!selectedTransferAdvisorId) {
      alert('Please select an advisor');
      return;
    }

    const selectedAdvisor = advisors.find(a => a.id === selectedTransferAdvisorId);
    if (!selectedAdvisor) {
      alert('Selected advisor not found');
      return;
    }

    setIsTransferring(true);
    try {
      const result = await ServiceAdvisorService.transferComplaintToAdvisor(
        id,
        user?.id,
        selectedTransferAdvisorId,
        selectedAdvisor.name || selectedAdvisor.email
      );

      if (result.success) {
        alert(result.message || 'Complaint transferred successfully');
        setShowTransferModal(false);
        setSelectedTransferAdvisorId('');
        refetch();
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
        queryClient.invalidateQueries(['serviceAdvisorComplaints', user?.id]);
      } else {
        alert('Failed to transfer: ' + (result.error || result.message));
      }
    } catch (error) {
      alert('Error transferring complaint: ' + error.message);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleUnassignAdvisor = async () => {
    if (!confirm('Are you sure you want to unassign yourself from this complaint?')) {
      return;
    }

    try {
      const result = await ServiceAdvisorService.unassignComplaint(id, user?.id);
      if (result.success) {
        alert(result.message || 'Complaint unassigned successfully');
        refetch();
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
        queryClient.invalidateQueries(['serviceAdvisorComplaints', user?.id]);
      } else {
        alert('Failed to unassign: ' + (result.error || result.message));
      }
    } catch (error) {
      alert('Error unassigning complaint: ' + error.message);
    }
  };

  const openImage = (url) => {
    setSelectedImage(url);
    setShowImageModal(true);
  };

  const handleUpdateStatus = () => {
    if (!newStatus) {
      alert('Please select a status');
      return;
    }
    updateStatusMutation.mutate({ status: newStatus, notes: statusNotes });
  };

  const handleUpdatePincode = () => {
    if (!pincode.trim()) {
      alert('Please enter a pincode');
      return;
    }
    updatePincodeMutation.mutate(pincode);
  };

  // Load advisors for transfer modal
  useEffect(() => {
    const loadAdvisors = async () => {
      try {
        const result = await ServiceAdvisorService.getAllAdvisors();
        if (result.success && result.data) {
          setAdvisors(result.data);
        }
      } catch (error) {
        console.error('Error loading advisors:', error);
      }
    };
    loadAdvisors();
  }, []);

  // Check if edit window is active (60 minutes after completion)
  useEffect(() => {
    if (complaint?.request_status === 'completed' && complaint?.completed_at) {
      const completedAt = new Date(complaint.completed_at);
      const now = new Date();
      const diffMs = now - completedAt;
      const diffSeconds = Math.floor(diffMs / 1000);
      const remaining = 3600 - diffSeconds; // 60 minutes = 3600 seconds
      
      if (remaining > 0) {
        setIsEditWindowActive(true);
        setEditRemainingSeconds(remaining);
        const interval = setInterval(() => {
          const newRemaining = 3600 - Math.floor((new Date() - completedAt) / 1000);
          if (newRemaining > 0) {
            setEditRemainingSeconds(newRemaining);
          } else {
            setIsEditWindowActive(false);
            setEditRemainingSeconds(0);
            clearInterval(interval);
          }
        }, 1000);
        return () => clearInterval(interval);
      } else {
        setIsEditWindowActive(false);
        setEditRemainingSeconds(0);
      }
    } else {
      setIsEditWindowActive(false);
      setEditRemainingSeconds(0);
    }
  }, [complaint?.request_status, complaint?.completed_at]);

  const formatMMSS = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const s = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const handleCompleteComplaint = async () => {
    if (!completionData.completionNotes.trim()) {
      alert('Please enter completion notes');
      return;
    }

    setUploadingCompletion(true);
    try {
      const result = await ServiceAdvisorService.updateComplaintCompletion(
        id,
        {
          completionNotes: completionData.completionNotes,
          customerQuotedAmount: completionData.customerQuotedAmount ? parseFloat(completionData.customerQuotedAmount) : undefined,
          advancePayment: completionData.advancePayment || undefined,
          mechanicPayment: completionData.mechanicPayment ? parseFloat(completionData.mechanicPayment) : undefined,
          completionMedia: completionData.completionMedia || undefined
        },
        user?.id,
        user?.name || user?.email
      );

      if (result.success) {
        alert(result.message || 'Service completed successfully!');
        setShowCompletionModal(false);
        setCompletionData({
          completionNotes: '',
          customerQuotedAmount: '',
          advancePayment: '',
          mechanicPayment: '',
          completionMedia: []
        });
        refetch();
        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
      } else {
        alert('Failed to complete service: ' + result.message);
      }
    } catch (error) {
      alert('Error completing service: ' + error.message);
    } finally {
      setUploadingCompletion(false);
    }
  };

  const handleAddCompletionMedia = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files);
      const fileUrls = files.map(file => URL.createObjectURL(file));
      setCompletionData(prev => ({
        ...prev,
        completionMedia: [...prev.completionMedia, ...fileUrls]
      }));
    };
    input.click();
  };

  const handleRemoveCompletionMedia = (index) => {
    setCompletionData(prev => ({
      ...prev,
      completionMedia: prev.completionMedia.filter((_, i) => i !== index)
    }));
  };

  const handleSaveFinancialEdit = async () => {
    try {
      const result = await ServiceAdvisorService.editCompletedComplaintWithinWindow(
        id,
        {
          customerQuotedAmount: financialEditData.customerQuotedAmount ? parseFloat(financialEditData.customerQuotedAmount) : undefined,
          advancePayment: financialEditData.advancePayment || undefined,
          mechanicPayment: financialEditData.mechanicPayment ? parseFloat(financialEditData.mechanicPayment) : undefined
        },
        user?.id,
        user?.name || user?.email
      );

      if (result.success) {
        alert('Financial details updated successfully!');
        setShowFinancialModal(false);
        refetch();
      } else {
        alert('Failed to update: ' + result.message);
      }
    } catch (error) {
      alert('Error updating financial details: ' + error.message);
    }
  };

  const handleSaveCustomerEdit = async () => {
    try {
      const result = await ServiceAdvisorService.editCompletedComplaintWithinWindow(
        id,
        {
          customerName: customerEditData.customerName || undefined,
          contactNumber: customerEditData.contactNumber || undefined,
          pincode: customerEditData.pincode || undefined
        },
        user?.id,
        user?.name || user?.email
      );

      if (result.success) {
        alert('Customer details updated successfully!');
        setShowCustomerModal(false);
        refetch();
      } else {
        alert('Failed to update: ' + result.message);
      }
    } catch (error) {
      alert('Error updating customer details: ' + error.message);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full spinner"></div>
      </div>
    );
  }

  if (!complaint || !complaint.id) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Complaint Not Found</h2>
          <p className="text-gray-600">The complaint with ID "{id}" could not be found.</p>
          <button onClick={() => navigate(-1)} className="mt-4 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      <div className="flex items-center space-x-4 mb-4">
        <button onClick={() => navigate(-1)} className="p-2 hover:bg-gray-100 rounded-lg">
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900">Complaint Details</h1>
          <p className="text-gray-600">#{complaint.id?.slice(0, 8)}</p>
        </div>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(complaint.request_status || complaint.status)}`}>
          {complaint.request_status || complaint.status || 'Unknown'}
        </span>
      </div>

      {/* Customer Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Customer Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center space-x-3">
            <User size={20} className="text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Name</p>
              <p className="font-medium text-gray-900">{complaint.customer_name || 'N/A'}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Phone size={20} className="text-gray-400" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Contact</p>
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900">
                  {complaint.contact_number || 'N/A'}
                </span>
                {complaint.contact_number && (
                  <>
                    <button onClick={() => copyToClipboard(complaint.contact_number, 'Phone number')} className="p-1 hover:bg-gray-100 rounded" title="Copy">
                    <Copy size={14} className="text-gray-400" />
                  </button>
                    <button onClick={() => handleCall(complaint.contact_number)} className="p-1 hover:bg-gray-100 rounded" title="Call">
                      <Phone size={14} className="text-blue-500" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
          {complaint.alternate_number && (
            <div className="flex items-center space-x-3">
              <Phone size={20} className="text-gray-400" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Alternate</p>
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">
                    {complaint.alternate_number}
                  </span>
                  <button onClick={() => copyToClipboard(complaint.alternate_number, 'Alternate number')} className="p-1 hover:bg-gray-100 rounded" title="Copy">
                    <Copy size={14} className="text-gray-400" />
                  </button>
                  <button onClick={() => handleCall(complaint.alternate_number)} className="p-1 hover:bg-gray-100 rounded" title="Call">
                    <Phone size={14} className="text-blue-500" />
                  </button>
                </div>
              </div>
            </div>
          )}
          <div className="flex items-start space-x-3 md:col-span-2">
            <MapPin size={20} className="text-gray-400 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Address</p>
              <p className="font-medium text-gray-900">
                {complaint.address && complaint.address.trim() !== '' 
                  ? complaint.address 
                  : (complaint.location_link 
                      ? 'Location link available (see below)' 
                      : (complaint.service_location || complaint.customer_location || complaint.pickup_location || complaint.drop_location || 'N/A'))}
              </p>
              {complaint.location_link && (
                <a 
                  href={complaint.location_link.startsWith('http') ? complaint.location_link : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(complaint.location_link)}`} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-teal-600 hover:underline text-sm flex items-center space-x-1 mt-1"
                >
                  <span>View on Map</span>
                  <ExternalLink size={14} />
                </a>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div>
              <p className="text-sm text-gray-500">Pincode</p>
              <div className="flex items-center space-x-2">
                <p className="font-medium text-gray-900">{complaint.pincode || 'Not set'}</p>
                <button
                  onClick={() => {
                    setPincode(complaint.pincode || '');
                    setShowPincodeModal(true);
                  }}
                  className="p-1 hover:bg-gray-100 rounded"
                  title="Update Pincode"
                >
                  <Edit size={14} className="text-gray-400" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Service Information */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Service Details</h2>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Wrench size={20} className="text-teal-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Problem:</p>
              <p className="font-medium text-gray-900">
                {complaint.problem || complaint.service_description || complaint.issue_description || 'Not specified'}
              </p>
          </div>
          </div>
          {complaint.source && (
            <div className="flex items-start space-x-3">
              <Calendar size={20} className="text-teal-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Source:</p>
                <p className="font-medium text-gray-900">
                  {complaint.source.replace(/_/g, ' ').toUpperCase()}
                </p>
            </div>
            </div>
          )}
          <div className="flex items-start space-x-3">
            <User size={20} className="text-teal-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Assigned Mechanic:</p>
              <p className="font-medium text-gray-900">
                {complaint.assigned_mechanic_name || complaint.mechanic_name || 'Not assigned'}
              </p>
              {assignedMechanicRow?.timing && (
                <p className="text-sm text-gray-600 mt-1">Timing: {assignedMechanicRow.timing}</p>
              )}
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Clock size={20} className="text-teal-500 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-gray-500">Registered:</p>
              <p className="font-medium text-gray-900">
                {formatDate(complaint.complaint_registered_at || complaint.register_datetime || complaint.requested_at || complaint.created_at)}
              </p>
            </div>
          </div>
          {complaint.remark && (
            <div className="flex items-start space-x-3">
              <Calendar size={20} className="text-teal-500 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm text-gray-500">Notes:</p>
                <p className="font-medium text-gray-900">{complaint.remark || 'N/A'}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Media */}
      {complaint.media_upload && complaint.media_upload.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Media</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {complaint.media_upload.map((url, index) => (
              <button
                key={index}
                onClick={() => openImage(url)}
                className="relative aspect-square rounded-lg overflow-hidden border border-gray-200 hover:border-teal-500 transition"
              >
                <img src={url} alt={`Media ${index + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Assignment Information */}
      {((complaint.assigned_mechanic_name || complaint.mechanic_name) || (complaint.payment_mechanic !== null && complaint.payment_mechanic !== undefined) || complaint.assigned_remark) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Mechanic Assignment Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {(complaint.assigned_mechanic_name || complaint.mechanic_name) && (
            <div>
              <p className="text-sm text-gray-500">Mechanic Name</p>
                <p className="font-medium text-gray-900">{complaint.assigned_mechanic_name || complaint.mechanic_name}</p>
            </div>
            )}
            {assignedMechanicRow?.timing && (
              <div>
                <p className="text-sm text-gray-500">Mechanic Timing</p>
                <p className="font-medium text-gray-900">{assignedMechanicRow.timing}</p>
              </div>
            )}
            {complaint.assigned_mechanic_contact && (
              <div>
                <p className="text-sm text-gray-500">Mechanic Contact</p>
                <div className="flex items-center space-x-2">
                <a href={`tel:${complaint.assigned_mechanic_contact}`} className="font-medium text-gray-900 hover:text-teal-600">
                  {complaint.assigned_mechanic_contact}
                </a>
                  <button onClick={() => copyToClipboard(complaint.assigned_mechanic_contact)} className="p-1 hover:bg-gray-100 rounded" title="Copy">
                    <Copy size={14} className="text-gray-400" />
                  </button>
                  <button onClick={() => handleCall(complaint.assigned_mechanic_contact)} className="p-1 hover:bg-gray-100 rounded" title="Call">
                    <Phone size={14} className="text-blue-500" />
                  </button>
                </div>
              </div>
            )}
            {(complaint.payment_mechanic !== null && complaint.payment_mechanic !== undefined) && (
              <div>
                <p className="text-sm text-gray-500">Mechanic Payment</p>
                <div className="flex items-center space-x-2">
                  <p className="font-medium text-gray-900">₹{complaint.payment_mechanic || '0'}</p>
                  <button onClick={() => copyToClipboard(`₹${complaint.payment_mechanic}`, 'Mechanic payment')} className="p-1 hover:bg-gray-100 rounded" title="Copy">
                    <Copy size={14} className="text-gray-400" />
                  </button>
                </div>
              </div>
            )}
            {complaint.assigned_remark && (
              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Assignment Remark</p>
                <p className="font-medium text-gray-900">{complaint.assigned_remark}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Financial Details Section - EXACTLY like mobile app */}
      {complaint.customer_quoted_amount && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Financial Details</h2>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <DollarSign size={20} className="text-teal-500" />
                <p className="text-sm text-gray-500">Quoted Amount:</p>
              </div>
              <div className="flex items-center space-x-2">
                <p className="font-medium text-gray-900">₹{complaint.customer_quoted_amount || '0'}</p>
                <button onClick={() => copyToClipboard(`₹${complaint.customer_quoted_amount}`, 'Quoted amount')} className="p-1 hover:bg-gray-100 rounded" title="Copy">
                  <Copy size={14} className="text-gray-400" />
                </button>
              </div>
            </div>
            {complaint.advance_payment && (
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <DollarSign size={20} className="text-teal-500" />
                  <p className="text-sm text-gray-500">Advance Payment:</p>
                </div>
                <p className="font-medium text-gray-900">₹{complaint.advance_payment || '0'}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Timeline */}
      {(() => {
        // Build comprehensive timeline like mobile app
        const buildComprehensiveTimeline = () => {
          const timelineEvents = [];
          
          if (!complaint) return timelineEvents;
          
          // 1. Complaint Registration
          if (complaint.register_datetime || complaint.requested_at || complaint.created_at) {
            timelineEvents.push({
              id: 'complaint-registered',
              status: 'Complaint Registered',
              timestamp: complaint.register_datetime || complaint.requested_at || complaint.created_at,
              actor_name: complaint.registered_by_executive_name || 'System',
              actor_role: 'Executive',
              notes: `Complaint registered for ${complaint.customer_name || 'Customer'}`,
              icon: '📝',
              color: '#3b82f6'
            });
          }
          
          // 2. Advisor Allocation
          if (complaint.assigned_to_advisor_at || complaint.assigned_advisor_id) {
            const isSelfAllocated = complaint.assigned_advisor_id === user?.id;
            timelineEvents.push({
              id: 'advisor-allocated',
              status: isSelfAllocated ? 'Self Allocated' : 'Allocated to Advisor',
              timestamp: complaint.assigned_to_advisor_at || complaint.register_datetime,
              actor_name: complaint.assigned_advisor_name || (isSelfAllocated ? user?.name : 'Advisor'),
              actor_role: 'Advisor',
              notes: isSelfAllocated ? 'You claimed this complaint' : `Allocated to ${complaint.assigned_advisor_name || 'Advisor'}`,
              icon: isSelfAllocated ? '👤' : '👥',
              color: isSelfAllocated ? '#10b981' : '#f59e0b'
            });
          }
          
          // 3. Mechanic Assignment
          if (complaint.mechanic_assigned_datetime || complaint.assigned_mechanic_id) {
            timelineEvents.push({
              id: 'mechanic-assigned',
              status: 'Mechanic Assigned',
              timestamp: complaint.mechanic_assigned_datetime || complaint.assigned_to_advisor_at,
              actor_name: complaint.assigned_mechanic_name || 'Mechanic',
              actor_role: 'Mechanic',
              notes: complaint.assigned_remark || `Mechanic ${complaint.assigned_mechanic_name || ''} assigned`,
              icon: '🔧',
              color: '#8b5cf6'
            });
          }
          
          // 4. Mechanic Reached
          if (complaint.mechanic_reached_datetime) {
            timelineEvents.push({
              id: 'mechanic-reached',
              status: 'Mechanic Reached',
              timestamp: complaint.mechanic_reached_datetime,
              actor_name: complaint.assigned_mechanic_name || 'Mechanic',
              actor_role: 'Mechanic',
              notes: complaint.reached_remark || 'Mechanic reached the location',
              icon: '📍',
              color: '#06b6d4'
            });
          }
          
          // 5. Work Started
          if (complaint.mechanic_started_datetime) {
            timelineEvents.push({
              id: 'mechanic-started',
              status: 'Work Started',
              timestamp: complaint.mechanic_started_datetime,
              actor_name: complaint.assigned_mechanic_name || 'Mechanic',
              actor_role: 'Mechanic',
              notes: complaint.mechanic_notes || 'Work started by mechanic',
              icon: '⚡',
              color: '#f59e0b'
            });
          }
          
          // 6. Work Completed
          if (complaint.mechanic_completed_datetime) {
            timelineEvents.push({
              id: 'mechanic-completed',
              status: 'Work Completed',
              timestamp: complaint.mechanic_completed_datetime,
              actor_name: complaint.assigned_mechanic_name || 'Mechanic',
              actor_role: 'Mechanic',
              notes: complaint.mechanic_completion_notes || complaint.complete_remark || 'Work completed by mechanic',
              icon: '✅',
              color: '#10b981'
            });
          }
          
          // 7. Service Completed
          if (complaint.completed_at || complaint.request_status === 'completed') {
            timelineEvents.push({
              id: 'service-completed',
              status: 'Service Completed',
              timestamp: complaint.completed_at || complaint.mechanic_completed_datetime,
              actor_name: complaint.assigned_advisor_name || user?.name || 'Advisor',
              actor_role: 'Advisor',
              notes: complaint.completion_notes || 'Service marked as completed',
              icon: '🎉',
              color: '#10b981'
            });
          }
          
          // 8. Service Cancelled
          if (complaint.cancelled_at || complaint.request_status === 'cancelled') {
            timelineEvents.push({
              id: 'service-cancelled',
              status: 'Service Cancelled',
              timestamp: complaint.cancelled_at,
              actor_name: complaint.assigned_advisor_name || complaint.cancelled_by || 'System',
              actor_role: 'Advisor',
              notes: complaint.cancelled_remark || complaint.cancellation_reason || 'Service cancelled',
              icon: '❌',
              color: '#ef4444'
            });
          }
          
          // Add timeline events from RPC (if any)
          if (Array.isArray(timeline) && timeline.length > 0) {
            timeline.forEach((event, index) => {
              // Avoid duplicates by checking if event already exists
              const exists = timelineEvents.some(e => 
                e.timestamp === (event.occurred_at || event.timestamp) &&
                e.status === (event.status || event.event)
              );
              
              if (!exists) {
                timelineEvents.push({
                  id: `timeline-${index}`,
                  status: event.status || event.event || 'Status Update',
                  timestamp: event.occurred_at || event.created_at || event.timestamp,
                  actor_name: event.actor_name || 'System',
                  actor_role: event.actor_role || 'System',
                  notes: event.notes || event.remark || event.description || '',
                  icon: '📋',
                  color: '#6b7280'
                });
              }
            });
          }
          
          // Sort by timestamp (oldest first)
          return timelineEvents.sort((a, b) => {
            const timeA = new Date(a.timestamp).getTime();
            const timeB = new Date(b.timestamp).getTime();
            return timeA - timeB;
          });
        };
        
        const comprehensiveTimeline = buildComprehensiveTimeline();
        
        if (comprehensiveTimeline.length === 0) {
          return null;
        }
        
        return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Status Timeline</h2>
          <ol className="relative border-l border-gray-200 ml-4">
              {comprehensiveTimeline.map((event, index) => (
                <li key={event.id || index} className="mb-6 ml-6">
                  <span 
                    className="absolute flex items-center justify-center w-6 h-6 rounded-full -left-3 ring-8 ring-white"
                    style={{ backgroundColor: `${event.color}20`, color: event.color }}
                  >
                    <span className="text-xs">{event.icon || '📋'}</span>
                </span>
                <h3 className="flex items-center mb-1 text-lg font-semibold text-gray-900">
                    {event.status}
                </h3>
                <time className="block mb-2 text-sm font-normal leading-none text-gray-400">
                    {formatDate(event.timestamp)}
                </time>
                {event.actor_name && (
                    <p className="text-sm text-gray-500 mb-1">
                      <span className="font-medium">{event.actor_role}:</span> {event.actor_name}
                    </p>
                  )}
                  {event.notes && event.notes.trim() !== '' && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">📝 Notes:</span> {event.notes}
                      </p>
                    </div>
                )}
              </li>
            ))}
          </ol>
        </div>
        );
      })()}

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-wrap gap-3">
          <button
            onClick={() => {
              if (!complaint.request_status || complaint.request_status === 'registered') {
                setNewStatus(complaint.request_status || 'registered');
              } else {
                setNewStatus(complaint.request_status);
              }
              setShowStatusModal(true);
            }}
            className="px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center justify-center space-x-2"
          >
            <Edit size={18} />
            <span>Update Status</span>
          </button>
          {!complaint.assigned_mechanic_id && (
            <button
              onClick={() => navigate(`/service_advisor/assign-mechanic/${id}`, {
                state: { complaint: complaint }
              })}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2"
            >
              <Wrench size={18} />
              <span>Assign Mechanic</span>
            </button>
          )}
          {complaint.assigned_mechanic_id && (
            <>
            <button
              onClick={() => navigate(`/service_advisor/assignment-details/${id}`)}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2"
            >
                <User size={18} />
                <span>View Assignment Details</span>
              </button>
              <button
                onClick={async () => {
                  if (confirm('Are you sure you want to unassign the current mechanic?')) {
                    try {
                      const result = await ServiceAdvisorService.unassignMechanicFromRequest(
                        id,
                        user?.id,
                        user?.name || user?.email
                      );
                      if (result.success) {
                        alert(result.message || 'Mechanic unassigned successfully');
                        refetch();
                        queryClient.invalidateQueries(['serviceAdvisorComplaintDetail', id]);
                      } else {
                        alert('Failed to unassign mechanic: ' + (result.error || result.message));
                      }
                    } catch (error) {
                      alert('Error unassigning mechanic: ' + error.message);
                    }
                  }
                }}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center justify-center space-x-2"
              >
                <X size={18} />
                <span>Unassign Mechanic</span>
              </button>
            </>
          )}
          {complaint.request_status !== 'completed' && complaint.request_status !== 'cancelled' && (
            <button
              onClick={() => {
                setCompletionData({
                  completionNotes: '',
                  customerQuotedAmount: complaint.customer_quoted_amount?.toString() || '',
                  advancePayment: complaint.advance_payment || '',
                  mechanicPayment: complaint.payment_mechanic?.toString() || '',
                  completionMedia: []
                });
                setShowCompletionModal(true);
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center space-x-2"
            >
              <CheckCircle size={18} />
              <span>Complete Service</span>
            </button>
          )}
          {isEditWindowActive && (
            <button
              onClick={() => setShowEditChooserModal(true)}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 flex items-center justify-center space-x-2"
            >
              <Edit size={18} />
              <span>Edit (remaining {formatMMSS(editRemainingSeconds)})</span>
            </button>
          )}
          {complaint.assigned_advisor_id === user?.id && (
            <>
              <button
                onClick={() => setShowTransferModal(true)}
                className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center justify-center space-x-2"
              >
                <User size={18} />
                <span>Transfer Complaint</span>
              </button>
              <button
                onClick={handleUnassignAdvisor}
                className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 flex items-center justify-center space-x-2"
              >
                <X size={18} />
                <span>Unassign Advisor</span>
              </button>
            </>
          )}
          {complaint.location_link && complaint.contact_number && (
            <button
              onClick={handleWhatsAppLocation}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center justify-center space-x-2"
            >
              <MessageCircle size={18} />
              <span>Send WhatsApp Location Request</span>
            </button>
          )}
          {complaint.contact_number && (
            <button
              onClick={() => handleCall(complaint.contact_number)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2"
            >
              <Phone size={18} />
              <span>Call Customer</span>
            </button>
          )}
          {complaint.assigned_mechanic_contact && (
            <button
              onClick={() => handleCall(complaint.assigned_mechanic_contact)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center justify-center space-x-2"
            >
              <Phone size={18} />
              <span>Call Mechanic</span>
            </button>
          )}
        </div>
      </div>

      {/* Update Status Modal */}
      {showStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Update Status</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select status...</option>
                  <option value="registered">Registered</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                <textarea
                  value={statusNotes}
                  onChange={(e) => setStatusNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Add notes (optional)..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowStatusModal(false);
                    setNewStatus('');
                    setStatusNotes('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateStatus}
                  disabled={!newStatus || updateStatusMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {updateStatusMutation.isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Update Pincode Modal */}
      {showPincodeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Update Pincode</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                <input
                  type="text"
                  value={pincode}
                  onChange={(e) => setPincode(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Enter pincode..."
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowPincodeModal(false);
                    setPincode('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdatePincode}
                  disabled={!pincode.trim() || updatePincodeMutation.isLoading}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {updatePincodeMutation.isLoading ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4" onClick={() => setShowImageModal(false)}>
          <div className="relative max-w-4xl max-h-[90vh]">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 text-white hover:text-gray-300"
            >
              <X size={24} />
            </button>
            <img src={selectedImage} alt="Media" className="max-w-full max-h-[90vh] rounded-lg" />
          </div>
        </div>
      )}

      {/* Complete Service Modal */}
      {showCompletionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Complete Service</h2>
              <button onClick={() => setShowCompletionModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Completion Notes *</label>
                <textarea
                  value={completionData.completionNotes}
                  onChange={(e) => setCompletionData(prev => ({ ...prev, completionNotes: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  placeholder="Describe the work completed..."
                />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Financial Details</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Customer Quoted (₹)</label>
                    <input
                      type="number"
                      value={completionData.customerQuotedAmount}
                      onChange={(e) => setCompletionData(prev => ({ ...prev, customerQuotedAmount: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Advance Payment (₹)</label>
                    <input
                      type="text"
                      value={completionData.advancePayment}
                      onChange={(e) => setCompletionData(prev => ({ ...prev, advancePayment: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Mechanic Payment (₹)</label>
                    <input
                      type="number"
                      value={completionData.mechanicPayment}
                      onChange={(e) => setCompletionData(prev => ({ ...prev, mechanicPayment: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Completion Media</h3>
                <button
                  onClick={handleAddCompletionMedia}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2"
                >
                  <Upload size={18} />
                  <span>Add Photos</span>
                </button>
                {completionData.completionMedia.length > 0 && (
                  <div className="mt-4 grid grid-cols-4 gap-2">
                    {completionData.completionMedia.map((url, index) => (
                      <div key={index} className="relative">
                        <img src={url} alt={`Media ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          onClick={() => handleRemoveCompletionMedia(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowCompletionModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCompleteComplaint}
                  disabled={!completionData.completionNotes.trim() || uploadingCompletion}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 flex items-center justify-center space-x-2"
                >
                  {uploadingCompletion ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                  <span>Complete Service</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Chooser Modal */}
      {showEditChooserModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Edit Complaint</h2>
              <button onClick={() => setShowEditChooserModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-3">
              <button
                onClick={() => {
                  setCustomerEditData({
                    customerName: complaint.customer_name || '',
                    contactNumber: complaint.contact_number || '',
                    pincode: complaint.pincode || ''
                  });
                  setShowEditChooserModal(false);
                  setShowCustomerModal(true);
                }}
                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-left"
              >
                <div className="font-semibold">Customer Details</div>
                <div className="text-sm opacity-90">Edit name, phone, pincode</div>
              </button>
              <button
                onClick={() => {
                  setFinancialEditData({
                    customerQuotedAmount: complaint.customer_quoted_amount?.toString() || '',
                    advancePayment: complaint.advance_payment || '',
                    mechanicPayment: complaint.payment_mechanic?.toString() || ''
                  });
                  setShowEditChooserModal(false);
                  setShowFinancialModal(true);
                }}
                className="w-full px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 text-left"
              >
                <div className="font-semibold">Financial Details</div>
                <div className="text-sm opacity-90">Edit quoted amount, payments</div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Financial Edit Modal */}
      {showFinancialModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Edit Financial Details</h2>
              <button onClick={() => setShowFinancialModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Quoted (₹)</label>
                <input
                  type="number"
                  value={financialEditData.customerQuotedAmount}
                  onChange={(e) => setFinancialEditData(prev => ({ ...prev, customerQuotedAmount: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Advance Payment (₹)</label>
                <input
                  type="text"
                  value={financialEditData.advancePayment}
                  onChange={(e) => setFinancialEditData(prev => ({ ...prev, advancePayment: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mechanic Payment (₹)</label>
                <input
                  type="number"
                  value={financialEditData.mechanicPayment}
                  onChange={(e) => setFinancialEditData(prev => ({ ...prev, mechanicPayment: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowFinancialModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveFinancialEdit}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center justify-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Customer Edit Modal */}
      {showCustomerModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Edit Customer Details</h2>
              <button onClick={() => setShowCustomerModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={24} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Customer Name</label>
                <input
                  type="text"
                  value={customerEditData.customerName}
                  onChange={(e) => setCustomerEditData(prev => ({ ...prev, customerName: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number</label>
                <input
                  type="text"
                  value={customerEditData.contactNumber}
                  onChange={(e) => setCustomerEditData(prev => ({ ...prev, contactNumber: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pincode</label>
                <input
                  type="text"
                  value={customerEditData.pincode}
                  onChange={(e) => setCustomerEditData(prev => ({ ...prev, pincode: e.target.value }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={() => setShowCustomerModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCustomerEdit}
                  className="flex-1 px-4 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 flex items-center justify-center space-x-2"
                >
                  <Save size={18} />
                  <span>Save</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Complaint Modal */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">Transfer Complaint</h2>
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedTransferAdvisorId('');
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={24} className="text-gray-500" />
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Advisor to Transfer To
              </label>
              <select
                value={selectedTransferAdvisorId}
                onChange={(e) => setSelectedTransferAdvisorId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select an advisor...</option>
                {advisors
                  .filter(advisor => advisor.id !== user?.id)
                  .map((advisor) => (
                    <option key={advisor.id} value={advisor.id}>
                      {advisor.name || advisor.email} {advisor.id === complaint.assigned_advisor_id ? '(Currently Assigned)' : ''}
                    </option>
                  ))}
              </select>
            </div>
            <div className="flex space-x-3 pt-4">
              <button
                onClick={() => {
                  setShowTransferModal(false);
                  setSelectedTransferAdvisorId('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={isTransferring}
              >
                Cancel
              </button>
              <button
                onClick={handleTransferComplaint}
                disabled={!selectedTransferAdvisorId || isTransferring}
                className="flex-1 px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isTransferring ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    <span>Transferring...</span>
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    <span>Transfer</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplaintDetail;

